import React, { useState, useEffect, useCallback } from 'react'

import { Checkbox } from '@progress/kendo-react-inputs'
import { GridColumn, GridCellProps } from '@progress/kendo-react-grid'
import styled from 'styled-components'
import {
  useLazyGetAdminNewsfeedTeamListQuery,
  useUpdateGroupNewsfeedTeamsMutation,
} from 'app/services/group.service'
import { FixMeLater, IDropdownOption, TDropdown } from 'types'
import KGrid from 'components/kendo/grid/KGrid'
import { icons } from 'utils/icons'
import DropdownListWithSearch from 'components/kendo/dropdown/DropdownListWithSearch'
import Tooltip from 'components/kendo/tooltips/Tooltip'
import CustomIcon from 'components/icon/CustomIcon'
import CustomText from 'components/text/CustomText'
import CustomButton from 'components/button/CustomButton'
import { useTranslation } from 'react-i18next'
import { BUTTONS, FORM, INFORMATION, PLACEHOLDERS, TABLE, TITLES } from 'locales/locales'

interface Team {
  _id?: string
  name: string
  canSeePosts?: boolean
  inEdit?: boolean
}

const NewsfeedTeamManagement = () => {
  const { t } = useTranslation()
  const [getAdminNewsfeedTeamList] = useLazyGetAdminNewsfeedTeamListQuery()
  const [updateGroupNewsfeedTeams] = useUpdateGroupNewsfeedTeamsMutation()

  // Grid Data States
  const [teamsGridData, setTeamsGridData] = useState<{ data: Team[]; total: number }>({
    data: [],
    total: 0,
  })

  // Grid State Management
  const [teamsDataState, setTeamsDataState] = useState({
    skip: 0,
    take: 10,
    sort: [],
    filter: { logic: 'and', filters: [] },
  })

  const [dropdownDataState, setDropdownDataState] = useState({
    skip: 0,
    take: 10,
    sort: [],
    filter: { logic: 'and', filters: [] },
  })

  const [dropdownData, setDropdownData] = useState<TDropdown>([])

  // Search States
  const [teamsSearchTerm, setTeamsSearchTerm] = useState('')

  const [selectedTeam, setSelectedTeam] = useState<IDropdownOption | null>({
    title: `${t(FORM.TEAM_SELECT)}...`,
    value: null,
  })
  const [loading, setLoading] = useState(false)

  const [originalTeamsData, setOriginalTeamsData] = useState<Team[]>([])
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    fetchTeams()
  }, [teamsDataState, selectedTeam])

  const fetchTeams = async () => {
    setLoading(true)
    try {
      const adminNewsfeedTeamList = (await getAdminNewsfeedTeamList({
        ...teamsDataState,
        teamId: selectedTeam?.value || '',
      })) as FixMeLater
      if (adminNewsfeedTeamList?.data?.success) {
        setTeamsGridData({
          data: adminNewsfeedTeamList.data.data,
          total: adminNewsfeedTeamList.data.totalCount,
        })
        // Orijinal veriyi sakla
        setOriginalTeamsData(adminNewsfeedTeamList.data.data)
        setHasChanges(false)
        setDropdownData(
          adminNewsfeedTeamList.data.data.map((team: FixMeLater) => ({
            title: team.name,
            value: team._id,
          })),
        )
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  const handleDropdownSearch = useCallback(async (skip: number, filter: string) => {
    setLoading(true)
    try {
      const filterConfig = filter
        ? { logic: 'and', filters: [{ field: 'name', operator: 'contains', value: filter }] }
        : { logic: 'and', filters: [] }

      const adminNewsfeedTeamList = (await getAdminNewsfeedTeamList({
        skip,
        take: 10,
        filter: filterConfig,
      })) as FixMeLater

      if (adminNewsfeedTeamList?.data?.success) {
        const mappedData = adminNewsfeedTeamList.data.data.map((team: FixMeLater) => ({
          title: team.name,
          value: team._id,
        }))

        return {
          data: mappedData,
          total: adminNewsfeedTeamList.data.totalCount || mappedData.length,
        }
      }

      return { data: [], total: 0 }
    } catch (error) {
      console.error('Error in handleDropdownSearch:', error)
      return { data: [], total: 0 }
    } finally {
      setLoading(false)
    }
  }, [])

  const updateTeamsGrid = (allTeams: Team[]) => {
    const filteredTeams = allTeams.filter((team) =>
      team.name.toLowerCase().includes(teamsSearchTerm.toLowerCase()),
    )

    setTeamsGridData({
      data: filteredTeams,
      total: filteredTeams.length,
    })
  }

  useEffect(() => {
    if (teamsGridData.data.length > 0) {
      updateTeamsGrid(teamsGridData.data)
    }
  }, [teamsSearchTerm])

  // Grid Event Handlers
  const handleTeamsDataStateChange = (e: FixMeLater) => {
    setTeamsDataState(e.dataState)
  }

  const handleTeamsInEditChange = (dataSource: FixMeLater) => {
    setTeamsGridData(dataSource)
  }

  const toggleTeamCanSeePost = (team: Team) => {
    if (!selectedTeam?.value) return
    setTeamsGridData((prev) => ({
      ...prev,
      data: prev.data.map((t: Team) =>
        t._id === team._id ? { ...t, canSeePosts: !t.canSeePosts } : t,
      ),
    }))
    setHasChanges(true)
  }

  // Custom Cell Renderers
  const CanSeePostsCell = (props: GridCellProps) => {
    const team = props.dataItem as Team
    return (
      <td>
        <StyledCheckbox
          checked={team.canSeePosts || false}
          onChange={() => toggleTeamCanSeePost(team)}
        />
      </td>
    )
  }

  const selectAllTeamsCanSeePosts = (checked: boolean) => {
    setTeamsGridData((prev) => ({
      ...prev,
      data: prev.data.map((team) => ({ ...team, canSeePosts: checked })),
    }))
    setHasChanges(true)
  }

  const handleSave = async () => {
    if (!selectedTeam?.value) return
    const response: any = await updateGroupNewsfeedTeams({
      teamId: selectedTeam?.value,
      data: {
        teamIds: teamsGridData.data.filter((team) => team.canSeePosts).map((team) => team._id),
      },
    })
    if (response?.data?.success) {
      setHasChanges(false)
    }
  }

  const handleCancel = () => {
    setTeamsGridData((prev) => ({
      ...prev,
      data: [...originalTeamsData],
    }))
    setHasChanges(false)
  }

  // Sadece paylaşım yapabilen takımları dropdown için filtrele
  const canSeePostsTeams = teamsGridData.data.filter((team) => team.canSeePosts)

  return (
    <Container>
      <Wrapper>
        <ContentContainer>
          <HeaderBox>
            <PageTitle>{t(TITLES.NEWSFEED_TEAM_MANAGEMENT)}</PageTitle>
          </HeaderBox>

          <GridsContainer>
            <TeamManagementPanel>
              <FilterContainer>
                <FilterLeft>
                  <StatisticsGroup>
                    <StatisticItem>
                      <CustomText typography="body6" color="#666">
                        {t(TITLES.TOTAL_TEAM)}:
                      </CustomText>
                      <CustomText typography="body5" color="#2a5885">
                        {teamsGridData.total}
                      </CustomText>
                    </StatisticItem>
                    <StatisticSeparator />
                    <StatisticItem>
                      <CustomText typography="body6" color="#666">
                        {t(TITLES.VISIBLE_TEAM)}:
                      </CustomText>
                      <CustomText typography="body5" color="#2a5885">
                        {canSeePostsTeams.length}
                      </CustomText>
                    </StatisticItem>
                  </StatisticsGroup>

                  <DropdownGroup>
                    <DropdownRow>
                      <DropdownLabelWithTooltip>
                        <CustomText typography="body3" color="#333">
                          {t(FORM.TEAM_SELECT)}:
                        </CustomText>
                        <Tooltip
                          position="top"
                          openDelay={300}
                          anchorElement="target"
                          parentTitle={true}
                          style={{
                            width: '200px',
                          }}
                          content={
                            <CustomIcon
                              iconPreset="medium"
                              source={icons.infoCircleIcon}
                              alt="Bilgi"
                              title={t(INFORMATION.NEWSFEED_TEAM_MANAGEMENT_INFO)}
                            />
                          }
                        />
                      </DropdownLabelWithTooltip>
                      <StyledDropDownList
                        selectedOption={
                          selectedTeam ||
                          ({ title: t(FORM.TEAM_SELECT), value: null } as IDropdownOption)
                        }
                        options={dropdownData}
                        onChangeOption={setSelectedTeam}
                        setFilterDataState={setDropdownDataState}
                        setDropdownData={setDropdownData}
                        filterDataState={dropdownDataState}
                        defaultItem={{ title: t(FORM.TEAM_SELECT) }}
                        requestData={handleDropdownSearch}
                        style={{ width: '100%' }}
                      />
                    </DropdownRow>
                  </DropdownGroup>
                </FilterLeft>
                <FilterRight>
                  <UserSearchInput
                    placeholder={t(PLACEHOLDERS.SEARCH_PLACEHOLDER)}
                    value={teamsSearchTerm}
                    onChange={(e) => setTeamsSearchTerm(String(e.target.value || ''))}
                  />
                  <ReloadButton onClick={fetchTeams}>
                    {loading ? (
                      <LoadingSpinner />
                    ) : (
                      <img
                        className="refresh"
                        height={15}
                        src={icons.refreshIcon.src}
                        srcSet={icons.refreshIcon.srcset}
                      />
                    )}
                  </ReloadButton>
                </FilterRight>
              </FilterContainer>

              <PanelContent>
                <GridContainer>
                  <KGrid
                    loading={loading}
                    datasource={teamsGridData.data}
                    total={teamsGridData.total}
                    dataState={teamsDataState}
                    onDataStateChange={handleTeamsDataStateChange}
                    onInEditChange={handleTeamsInEditChange}
                    filterable={false}
                    sortable={false}
                    pageable={true}
                    style={{ height: '100%' }}
                  >
                    <GridColumn
                      field="canSeePosts"
                      title={t(TITLES.VISIBLE_TEAM)}
                      width="200px"
                      headerCell={() => (
                        <div
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            justifyContent: 'start',
                          }}
                        >
                          <StyledCheckbox
                            checked={teamsGridData.data.every((team) => team.canSeePosts === true)}
                            onChange={(e) => selectAllTeamsCanSeePosts(e.value)}
                          />
                          {t(TITLES.VISIBLE_TEAM)}
                        </div>
                      )}
                      cells={{ data: CanSeePostsCell }}
                      filterable={false}
                    />
                    <GridColumn
                      field="name"
                      title={t(TABLE.TEAM_NAME)}
                      filterable={false}
                      sortable={false}
                    />
                  </KGrid>
                </GridContainer>

                {/* Kaydet ve İptal Butonları */}
                <ActionButtonsContainer>
                  <CancelButton
                    text={t(BUTTONS.CANCEL)}
                    onClick={handleCancel}
                    disabled={!hasChanges}
                  />
                  <SaveButton text={t(BUTTONS.SAVE)} onClick={handleSave} disabled={!hasChanges} />
                </ActionButtonsContainer>
              </PanelContent>
            </TeamManagementPanel>
          </GridsContainer>
        </ContentContainer>
      </Wrapper>
    </Container>
  )
}

export default NewsfeedTeamManagement

const ContentContainer = styled.div`
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  background-color: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`

const HeaderBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  border-bottom: 1px solid #e6e6e6;
`

// Styled Components

const PageTitle = styled.h1`
  color: #2a5885;
  font-size: 1.75rem;
  margin: 0;
  font-weight: 600;
`

const GridsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0;
  flex: 1;
`

const StyledCheckbox = styled(Checkbox)`
  .k-checkbox {
    margin: 0;
  }
`

const TeamManagementPanel = styled.div`
  border: 1px solid #e3ebf6;
  border-radius: 4px;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
`

const FilterContainer = styled.div`
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e3ebf6;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 16px;
  }
`

const FilterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 8px;
  }
`

const FilterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;

  @media (max-width: 1024px) {
    flex: none;
    width: 100%;
    margin-left: 0;
  }
`

const PanelContent = styled.div`
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
`

const StatisticsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`

const StatisticItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  text-align: center;
`

const StatisticSeparator = styled.div`
  width: 1px;
  height: 32px;
  background-color: #e0e0e0;
`

const DropdownGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 280px;

  @media (max-width: 1024px) {
    min-width: 100%;
  }
`

const DropdownRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`

const UserSearchInput = styled.input`
  padding: 6px 12px;
  border: 1px solid #e0e0e0;
  height: 30px;
  border-radius: 4px;
  background-color: white;
  width: 130px;
  font-size: 11px;
  &::placeholder {
    color: #888;
  }
  &:focus {
    outline: none;
    border-color: #129fdb;
    box-shadow: 0 0 0 2px rgba(18, 159, 219, 0.1);
  }
`

const ReloadButton = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
`

const LoadingSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const GridContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  .k-grid {
    border: 1px solid #e0e0e0;

    .k-grid-header {
      position: sticky;
      top: 0;
      z-index: 2;
      background-color: white;
      .k-header {
        height: 30px !important;
      }
    }

    .k-grid-content {
      flex: 1;
      overflow-y: auto;
    }

    .k-pager-wrap {
      position: sticky;
      bottom: 0;
      z-index: 2;
      background-color: white;
      border-top: 1px solid #e0e0e0;
    }
  }
`

const DropdownLabelWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-height: 32px;
`

const StyledDropDownList = styled(DropdownListWithSearch)`
  width: 200px;
  border-radius: 4px;

  .k-picker {
    border: 1px solid #ddd;
    height: 30px;
  }

  .k-input-inner {
    padding: 0.25rem 0.5rem;
    font-size: 11px;
  }

  &:focus {
    outline: 2px solid #90caf9;
    outline-offset: 2px;
  }
`

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`

const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-top: 1px solid #e3ebf6;
  margin-top: auto;
`

const SaveButton = styled(CustomButton)`
  background-color: #1e4ab7;
`

const CancelButton = styled(CustomButton)`
  background-color: #6c757d;
`
